import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Autocomplete, Chip, DialogContentText, Grid, IconButton, MenuItem, Switch, TextField, Tooltip } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormValues } from '../../../../redux/slices/reports';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import {
  FIRST_DAY_OF_MONTH_FREQUENCY_IDS,
  TRANSPORT_REQUEST_REPORT_ID,
  TRANSPORT_REPORT_IDS,
  TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID,
} from '../Constants';

export default function ReportScheduleStep() {
  const dispatch = useDispatch();
  const { formValues, createReportData, reportRecipients, selectedReportId } = useSelector(
    (state) => state.reports.data
  );
  const isTransportRequestReport = selectedReportId === TRANSPORT_REQUEST_REPORT_ID;
  const isTransportTripsByDoctorTypeReport = selectedReportId === TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID;
  const isTransportReportType = TRANSPORT_REPORT_IDS.includes(selectedReportId);
  const { frequencies, dateFilters, ftpSites } = createReportData;
  const [emails, setEmails] = useState([]);
  const [filterOn, setFilterOn] = useState(false);
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    setEmails(
      reportRecipients.map((user) => ({
        value: user.alternateId,
        label: `${user.firstName} ${user.lastName}`,
        email: user.email,
        facilityId: user.facilityId,
      }))
    );
  }, [reportRecipients]);

  useEffect(() => {
    if (!Object.prototype.hasOwnProperty.call(formValues, 'active')) {
      dispatch(updateFormValues({ ...formValues, active: true }));
    }
  }, [formValues, dispatch]);

  const recipientsOptions = useMemo(() => {
    const facs = facilities.map((f) => f.value);

    const filteredEmails = emails?.filter((r) => !facs?.length || facs.includes(r.facilityId));

    return [...filteredEmails].sort((a, b) => a.label.localeCompare(b.label));
  }, [facilities, emails]);

  const sortedFrequencies = useMemo(
    () =>
      (frequencies || [])
        .filter(
          (frequency) => !isTransportTripsByDoctorTypeReport || FIRST_DAY_OF_MONTH_FREQUENCY_IDS.includes(frequency.id)
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [frequencies, isTransportTripsByDoctorTypeReport]
  );

  const filteredDateFilters = useMemo(
    () =>
      (dateFilters ?? []).filter((filter) =>
        isTransportRequestReport ? Boolean(filter?.isTransportFilter) : !filter?.isTransportFilter
      ),
    [dateFilters, isTransportRequestReport]
  );

  return (
    <>
      <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Active</DialogContentText>
      <Switch
        checked={formValues.active ?? false}
        onChange={(e) => dispatch(updateFormValues({ ...formValues, active: e.target.checked }))}
      />
      <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
        Frequency{' '}
        <Tooltip title="Choose a time slot for the report to go out on">
          <Iconify icon="material-symbols:info-outline" width={14} height={14} color="info" />
        </Tooltip>
      </DialogContentText>
      <Autocomplete
        size="small"
        options={sortedFrequencies}
        getOptionKey={(o) => o.id}
        getOptionLabel={(o) => o.name}
        value={formValues?.frequency ?? null}
        onChange={(_, newValue) => dispatch(updateFormValues({ ...formValues, frequency: newValue }))}
        renderInput={(params) => <TextField {...params} size="small" variant="outlined" autoComplete="off" />}
      />
      {isTransportTripsByDoctorTypeReport ? null : selectedReportId !== 12 && selectedReportId !== 15 ? (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            Time Frame{' '}
            <Tooltip title="Choose how far back the report data should extend to">
              <Iconify icon="material-symbols:info-outline" width={14} height={14} color="info" />
            </Tooltip>
          </DialogContentText>
          <Autocomplete
            size="small"
            options={filteredDateFilters}
            getOptionKey={(o) => o.id}
            getOptionLabel={(o) => o.name}
            value={filteredDateFilters.find((o) => o.id === formValues?.timeFrame?.id) || null}
            onChange={(_, newValue) => dispatch(updateFormValues({ ...formValues, timeFrame: newValue }))}
            renderInput={(params) => <TextField {...params} size="small" variant="outlined" autoComplete="off" />}
          />
        </>
      ) : (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Time Frame</DialogContentText>
          <TextField disabled value="For Today" size="small" fullWidth variant="outlined" autoComplete="off" />
        </>
      )}
      <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
        Recipients
        <IconButton onClick={() => setFilterOn((o) => !o)}>
          {filterOn ? <FilterAltOffIcon color="warning" /> : <FilterAltIcon color="info" />}
        </IconButton>
      </DialogContentText>
      {filterOn && (
        <Grid container sx={{ mb: 2 }} spacing={1}>
          {createReportData.companies && (
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                disableCloseOnSelect
                size="small"
                multiple
                options={createReportData.companies}
                value={groups || []}
                onChange={(_, newValue) => {
                  setGroups(newValue);
                  const compIds = newValue.map((v) => v.value);
                  setFacilities(createReportData.facilities?.filter((f) => compIds?.includes(f.companyId)));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    label="Filter by group..."
                    autoComplete="off"
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              disableCloseOnSelect
              size="small"
              multiple
              options={createReportData.facilities || []}
              value={facilities}
              limitTags={2}
              onChange={(_, newValue) => {
                setFacilities(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by facility..."
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      <Autocomplete
        freeSolo
        disableCloseOnSelect
        size="small"
        multiple
        sx={{
          '& .MuiAutocomplete-option.Mui-disabled': {
            opacity: 1,
            color: 'text.primary',
            WebkitTextFillColor: (theme) => theme.palette.text.primary,
          },
        }}
        options={
          recipientsOptions.length > 0
            ? [{ label: 'Select All', email: 'all', isSelectAll: true }, ...recipientsOptions]
            : [{ label: "No recipients match this user's permissions", noRecipients: true }]
        }
        value={formValues?.recipients || []}
        onChange={(_, newValue) => {
          if (newValue.some((v) => v.noRecipients)) return;
          const allOptions = recipientsOptions;
          if (newValue.some((v) => v.isSelectAll)) {
            // If all are already selected, deselect all
            if ((formValues?.recipients?.length || 0) === allOptions.length) {
              dispatch(updateFormValues({ ...formValues, recipients: [] }));
            } else {
              dispatch(updateFormValues({ ...formValues, recipients: allOptions }));
            }
          } else {
            // Remove Select All if present in newValue
            const filtered = newValue.filter((v) => !v.isSelectAll);
            dispatch(updateFormValues({ ...formValues, recipients: filtered }));
          }
        }}
        getOptionKey={(params) =>
          params?.isSelectAll ? 'select-all' : params?.label ? `${params?.label} - ${params?.email}` : params
        }
        getOptionLabel={(params) =>
          params?.noRecipients
            ? "There are no recipients for this user's permissions"
            : params?.isSelectAll
            ? 'Select All'
            : params?.label
            ? `${params?.label} - ${params?.email}`
            : params
        }
        getOptionDisabled={(option) => option?.noRecipients === true}
        renderTags={(selectedOptions, getTagProps) =>
          selectedOptions.map((option, index) => (
            <Chip
              size="small"
              key={option.label ?? option}
              label={option.label ?? option}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => <TextField {...params} size="small" variant="outlined" autoComplete="off" />}
      />
      {!isTransportReportType && (
        <>
          <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
            Ftp{' '}
            <Tooltip title="Choose Ftp Location">
              <Iconify icon="material-symbols:info-outline" width={14} height={14} color="info" />
            </Tooltip>
          </DialogContentText>
          <Autocomplete
            size="small"
            options={ftpSites}
            getOptionLabel={(option) => option.label}
            value={ftpSites.find((site) => site.id === formValues?.ftpSiteId) || null}
            onChange={(_, newValue) => dispatch(updateFormValues({ ...formValues, ftpSiteId: newValue?.id || null }))}
            renderInput={(params) => <TextField {...params} size="small" variant="outlined" autoComplete="off" />}
          />
        </>
      )}
      {formValues.selectedReport?.supportsCsv && (
        <TextField
          select
          fullWidth
          size="small"
          label="Report format"
          helperText="Applies to both email attachments and FTP uploads."
          sx={{ mt: 2, mb: 2 }}
          value={formValues.downloadAsCsv ? 'csv' : 'xlsx'}
          onChange={(event) =>
            dispatch(updateFormValues({ ...formValues, downloadAsCsv: event.target.value === 'csv' }))
          }
        >
          <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
          <MenuItem value="csv">CSV (.csv)</MenuItem>
        </TextField>
      )}
    </>
  );
}
